import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from 'api/client';

// ── Query keys ───────────────────────────────────────────────────────────────
export const pipelineKeys = {
  all: ['pipelines'],
  pipeline: (id) => ['pipelines', id],
  runs: (id) => ['pipelines', id, 'runs'],
  backfills: (id) => ['pipelines', id, 'backfills'],
  run: (id) => ['pipeline-runs', id],
  health: ['pipelines', 'health'],
  healthOne: (id) => ['pipelines', id, 'health'],
};

// Periods are cut in the viewer's own zone, so "a day" matches their calendar.
const viewerZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

// ── Pipelines ─────────────────────────────────────────────────────────────────
export function usePipelines() {
  return useQuery({
    queryKey: pipelineKeys.all,
    queryFn: () => apiClient.get('/api/v1/pipelines/pipelines/').then((r) => r.data),
    refetchInterval: 60000,
  });
}

export function usePipeline(id) {
  return useQuery({
    queryKey: pipelineKeys.pipeline(id),
    queryFn: () => apiClient.get(`/api/v1/pipelines/pipelines/${id}/`).then((r) => r.data),
    enabled: !!id,
  });
}

export function useCreatePipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      apiClient.post('/api/v1/pipelines/pipelines/', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: pipelineKeys.all }),
  });
}

export function useUpdatePipeline(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      apiClient.patch(`/api/v1/pipelines/pipelines/${id}/`, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pipelineKeys.all });
      qc.invalidateQueries({ queryKey: pipelineKeys.pipeline(id) });
    },
  });
}

export function useDeletePipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiClient.delete(`/api/v1/pipelines/pipelines/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: pipelineKeys.all }),
  });
}

export function useTogglePipeline(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiClient.patch(`/api/v1/pipelines/pipelines/${id}/toggle/`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pipelineKeys.all });
      qc.invalidateQueries({ queryKey: pipelineKeys.pipeline(id) });
    },
  });
}

export function useTriggerPipeline(id) {
  const qc = useQueryClient();
  return useMutation({
    // Incremental only: full refresh is deliberately not exposed — historical
    // loads go through the sliced Backfill flow instead.
    mutationFn: () =>
      apiClient.post(`/api/v1/pipelines/pipelines/${id}/trigger/`).then((r) => r.data),
    onSuccess: () => {
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: pipelineKeys.runs(id) });
        qc.invalidateQueries({ queryKey: pipelineKeys.healthOne(id) });
      }, 2000);
    },
  });
}

// ── Backfills ─────────────────────────────────────────────────────────────────
export function useBackfillPipeline(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      apiClient.post(`/api/v1/pipelines/pipelines/${id}/backfill/`, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pipelineKeys.backfills(id) });
      qc.invalidateQueries({ queryKey: pipelineKeys.runs(id) });
    },
  });
}

export function usePipelineBackfills(pipelineId) {
  return useQuery({
    queryKey: pipelineKeys.backfills(pipelineId),
    queryFn: () =>
      apiClient.get(`/api/v1/pipelines/pipelines/${pipelineId}/backfills/`).then((r) => r.data),
    enabled: !!pipelineId,
    refetchInterval: 5000, // poll while page is open to catch slice progress
  });
}

export function useCancelBackfillJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (jobId) =>
      apiClient.post(`/api/v1/pipelines/backfills/${jobId}/cancel/`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: pipelineKeys.all }),
  });
}

export function useRetryBackfillJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (jobId) =>
      apiClient.post(`/api/v1/pipelines/backfills/${jobId}/retry/`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: pipelineKeys.all }),
  });
}

// ── Health: status word and the last 14 planned periods ───────────────────────
export function usePipelinesHealth() {
  return useQuery({
    queryKey: pipelineKeys.health,
    queryFn: () =>
      apiClient
        .get('/api/v1/pipelines/pipelines/health/', { params: { tz: viewerZone() } })
        .then((r) => r.data),
    refetchInterval: 60000,
  });
}

export function usePipelineHealth(id) {
  return useQuery({
    queryKey: pipelineKeys.healthOne(id),
    queryFn: () =>
      apiClient
        .get(`/api/v1/pipelines/pipelines/${id}/health/`, { params: { tz: viewerZone() } })
        .then((r) => r.data),
    enabled: !!id,
    refetchInterval: 30000,
  });
}

// ── Pipeline Runs ─────────────────────────────────────────────────────────────
/** The newest `limit` runs ({count, results}); the page asks for more by raising it. */
export function usePipelineRuns(pipelineId, limit = 15) {
  return useQuery({
    queryKey: [...pipelineKeys.runs(pipelineId), limit],
    queryFn: () =>
      apiClient
        .get(`/api/v1/pipelines/pipelines/${pipelineId}/runs/`, { params: { limit } })
        .then((r) => r.data),
    enabled: !!pipelineId,
    placeholderData: (previous) => previous,
    refetchInterval: 5000, // poll while page is open to catch run updates
  });
}

export function usePipelineRun(runId) {
  return useQuery({
    queryKey: pipelineKeys.run(runId),
    queryFn: () =>
      apiClient.get(`/api/v1/pipelines/runs/${runId}/`).then((r) => r.data),
    enabled: !!runId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 3000;
      if (data.status === 'success' || data.status === 'failed' || data.status === 'cancelled') return false;
      return 3000;
    },
  });
}
